// The shell shared by the memory side of Studio. Three tabs, one per JOB a person comes here to do:
//   Memory      (/memory)  — what was captured, what it did for you, the tree, the spaces.
//   Connections (/connect) — where Assertion is running: your plugin clients and your agent installs,
//                            with versions, plus the key and install commands.
//   Account     (/account) — money, in one place: your agent plan, and your team's seats.
// NOT tabs by product. The agent and the plugin share one memory and one team, and differ only in
// the software you install, so a product-shaped tab bar splits billing across two tabs and leaves a
// product tab holding nothing but an install. Both products appear INSIDE Connections (as two
// sections) and inside Account (as an agent plan and the seat kinds). Deliberately NOT
// the analytics app shell — these routes live outside the analytics "/" layout (see app.js). The
// logo goes to /memory, the home; "Analytics" on the right is the product switcher, the mirror of
// the "Memory" entry in the analytics sidebar (layouts/Main.jsx), so either shell reaches the other
// in one click.
import { useIsAuthenticated } from "@azure/msal-react";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Box, Button, Menu, MenuItem, Typography, Divider, CircularProgress } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import AddIcon from "@mui/icons-material/Add";
import Logo from "../assets/icons/logo_full.png";
import { useImpersonation } from "../contexts/ImpersonationContext";
import { listAdminWorkspaces, getActingWorkspaceId, setActingWorkspaceId } from "../utils/api";

const TEAL = "#2da894";

// User-facing space switcher ("Space: Personal ▾") — a pure VIEW selector: picking a space
// only changes what the /memory page shows (URL ?space=, read-only, member-validated
// server-side). It never writes routing — "Contribute here" on the page's SpaceBar is the
// only routing action. COMPLETELY SEPARATE state channel from AdminOrgSwitcher below (which
// stays explicit ?ws + X-Acting-Org, admin-only). Renders only when the page passes space
// data (i.e. /memory with an authed user, not impersonating).
function SpaceSwitcher({ spaceState, viewedWorkspace, onSelectSpace, onOpenTeamPanel }) {
  const [anchorEl, setAnchorEl] = useState(null);
  // "New space" dot: spaces this browser hasn't seen yet (e.g. a teammate added you).
  // Client-side seen-tracking only — the terminal-side one-shot notice is the recap line.
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(window.localStorage.getItem("seen_team_spaces") || "[]")); }
    catch (_) { return new Set(); }
  });
  if (!spaceState) return null;
  const { active, spaces = [] } = spaceState;
  const viewed = spaces.find((s) => s.workspace === viewedWorkspace) || null;
  const hasNew = spaces.some((s) => !seen.has(s.workspace));

  const openMenu = (e) => {
    setAnchorEl(e.currentTarget);
    // Opening the menu acknowledges the new spaces.
    const all = new Set(spaces.map((s) => s.workspace));
    setSeen(all);
    try { window.localStorage.setItem("seen_team_spaces", JSON.stringify([...all])); } catch (_) {}
  };
  const close = () => setAnchorEl(null);
  const choose = (ws) => { close(); onSelectSpace(ws); };   // view-only: URL change, no writes

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <Button
        onClick={openMenu}
        size="small"
        startIcon={<FolderSharedOutlinedIcon fontSize="small" />}
        sx={{
          textTransform: "none", fontWeight: 600, fontSize: 13,
          color: viewed ? TEAL : "#9aa0aa",
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        }}
      >
        <Box component="span" sx={{ display: "inline-block", maxWidth: { xs: 120, md: 260 }, overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
          {viewed ? `Space: ${viewed.name || viewed.workspace}` : "Space: Personal"}
        </Box>
        {hasNew && (
          <Box component="span" sx={{ ml: 0.75, width: 7, height: 7, borderRadius: "50%",
                                      bgcolor: TEAL, display: "inline-block" }} />
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        PaperProps={{ sx: { maxHeight: 440, minWidth: 260, bgcolor: "#15151c",
                            color: "#e6e6ea", border: "1px solid #26262f" } }}
      >
        <MenuItem onClick={() => choose(null)} sx={{ fontWeight: viewed ? 400 : 700 }}>
          {!viewed && <CheckIcon fontSize="small" sx={{ mr: 1, color: TEAL }} />}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2">Personal</Typography>
            <Typography variant="caption" sx={{ color: "#7a7f8a" }}>your private memory</Typography>
          </Box>
        </MenuItem>
        {spaces.length > 0 && <Divider sx={{ borderColor: "#26262f" }} />}
        {spaces.map((s) => {
          const isViewed = s.workspace === viewedWorkspace;
          const isRouted = s.workspace === active;   // where the user's SESSIONS write
          return (
            <MenuItem key={s.workspace} onClick={() => choose(s.workspace)}
                      sx={{ fontWeight: isViewed ? 700 : 400 }}>
              {isViewed && <CheckIcon fontSize="small" sx={{ mr: 1, color: TEAL }} />}
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography variant="body2" noWrap>{s.name || s.workspace}</Typography>
                <Typography variant="caption" sx={{ color: isRouted ? TEAL : "#7a7f8a" }} noWrap>
                  {s.member_count} member{s.member_count !== 1 ? "s" : ""}
                  {isRouted ? " · your sessions' default" : ""}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
        <Divider sx={{ borderColor: "#26262f" }} />
        <MenuItem onClick={() => { close(); onOpenTeamPanel(); }}>
          <AddIcon fontSize="small" sx={{ mr: 1, color: TEAL }} />
          <Typography variant="body2" sx={{ color: TEAL, fontWeight: 600 }}>
            {spaces.length ? "Manage spaces…" : "Create a team space…"}
          </Typography>
        </MenuItem>
      </Menu>

    </Box>
  );
}

// Admin-only: inspect the memory product AS another org AND (for isolated orgs) a specific
// user's workspace. Two-level: pick an org → pick a user/workspace. Reuses the shared
// ImpersonationContext for the org (same acting-org the analytics OrgSwitcher sets) and the
// memory-only acting-WORKSPACE (api.js, sent as ?ws on /me/* calls only). Renders nothing for
// non-admins, so the nav is unchanged for normal users.
function AdminOrgSwitcher() {
  const isAuth = useIsAuthenticated();
  const {
    adminStatus, adminProbeFailed, orgs, actingOrgId, actingOrg, setActingOrg, clearActing,
    retryAdminProbe,
  } = useImpersonation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [level, setLevel] = useState("orgs");      // "orgs" | "workspaces"
  const [pickedOrg, setPickedOrg] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWs, setLoadingWs] = useState(false);
  // Same rule as the analytics OrgSwitcher: hide ONLY on an authoritative non-admin answer.
  // "Still checking" and "probe failed" keep the control visible so it can't silently vanish
  // mid-session (which is what forced the refresh-until-it-appears workaround).
  // Staff-only control: shown to a signed-in admin (the per-browser hint or the probe said so), and to
  // nobody else. It used to hide only on an authoritative "not an admin", so a signed-out visitor —
  // never probed — and anyone on a fresh browser saw a "Checking access…" button that never resolved.
  // A known admin still keeps it through a failed probe (the retry label), which was the point.
  if (!isAuth || adminStatus !== "yes") return null;
  const pending = false;

  const actingWs = getActingWorkspaceId();
  const acting = Boolean(actingOrgId);
  // Prefer the domain, fall back to the raw org id — the ?ws / X-Acting-Org scoping is live
  // either way, so the label must never claim "self" just because the org list failed.
  const orgLabel = actingOrg?.domain || actingOrgId;
  const label = actingWs ? `${orgLabel} / ${actingWs}` : orgLabel;

  const close = () => { setAnchorEl(null); setLevel("orgs"); setPickedOrg(null); setWorkspaces([]); };
  const reload = () => window.location.reload();   // re-fetch tree + receipts under the new scope
  const chooseSelf = () => { clearActing(); setActingWorkspaceId(null); close(); reload(); };

  const openOrg = async (o) => {
    setPickedOrg(o); setLevel("workspaces"); setLoadingWs(true); setWorkspaces([]);
    try {
      const data = await listAdminWorkspaces(o.org_id);
      setWorkspaces(data.workspaces || []);
    } catch (_) {
      setWorkspaces([]);
    } finally { setLoadingWs(false); }
  };

  const chooseWorkspace = (ws) => {
    setActingOrg(pickedOrg.org_id);   // NB: clears any prior acting-workspace
    setActingWorkspaceId(ws);         // then pin the specific workspace
    close(); reload();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Button
        onClick={(e) => {
          if (adminProbeFailed) { retryAdminProbe(); return; }
          if (pending) return;
          setAnchorEl(e.currentTarget);
        }}
        size="small"
        startIcon={<SwapHorizIcon fontSize="small" />}
        sx={{
          textTransform: "none", fontWeight: 600, fontSize: 13,
          color: acting ? "#2da894" : "#9aa0aa",
          opacity: pending ? 0.6 : 1,
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        }}
      >
        {adminProbeFailed
          ? "Viewing: unavailable — retry"
          : pending
            ? "Checking access…"
            : actingOrgId ? `Viewing: ${label}` : "Viewing: self"}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        PaperProps={{ sx: { maxHeight: 440, minWidth: 280, bgcolor: "#15151c",
                            color: "#e6e6ea", border: "1px solid #26262f" } }}
      >
        {level === "orgs" && [
          <MenuItem key="__self" onClick={chooseSelf} sx={{ fontWeight: acting ? 400 : 700 }}>
            {!acting && <CheckIcon fontSize="small" sx={{ mr: 1, color: "#2da894" }} />}
            <Typography variant="body2">View as myself</Typography>
          </MenuItem>,
          <Divider key="__div" sx={{ borderColor: "#26262f" }} />,
          ...orgs.map((o) => (
            <MenuItem key={o.org_id} onClick={() => openOrg(o)}
              sx={{ fontWeight: o.org_id === actingOrgId ? 700 : 400 }}>
              {o.org_id === actingOrgId && <CheckIcon fontSize="small" sx={{ mr: 1, color: "#2da894" }} />}
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{o.domain || "(no domain)"}</Typography>
                <Typography variant="caption" sx={{ color: "#7a7f8a" }} noWrap>{o.org_id}</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#7a7f8a", ml: 1 }}>›</Typography>
            </MenuItem>
          )),
          orgs.length === 0 ? (
            <MenuItem key="__none" disabled>
              <Typography variant="caption" sx={{ color: "#7a7f8a" }}>No orgs found</Typography>
            </MenuItem>
          ) : null,
        ]}

        {level === "workspaces" && [
          <MenuItem key="__back" onClick={() => { setLevel("orgs"); setPickedOrg(null); }}>
            <ArrowBackIcon fontSize="small" sx={{ mr: 1, color: "#9aa0aa" }} />
            <Typography variant="body2" noWrap>{pickedOrg?.domain} — pick a user</Typography>
          </MenuItem>,
          <Divider key="__div2" sx={{ borderColor: "#26262f" }} />,
          loadingWs ? (
            <MenuItem key="__load" disabled>
              <CircularProgress size={14} sx={{ mr: 1, color: "#2da894" }} />
              <Typography variant="caption" sx={{ color: "#7a7f8a" }}>Loading spaces…</Typography>
            </MenuItem>
          ) : null,
          ...(!loadingWs ? workspaces.map((w) => {
            const isCur = pickedOrg?.org_id === actingOrgId && w.workspace === actingWs;
            // Team spaces are labeled as spaces (name · members · empty), not as their
            // creator's email — an admin inspecting an org must be able to tell a shared
            // tree from a member's personal home at a glance.
            const label = w.is_team ? (w.name || w.workspace) : (w.owner_email || w.workspace);
            const caption = w.is_team
              ? [`team space · ${w.member_count || 1} member${(w.member_count || 1) !== 1 ? "s" : ""}`,
                 w.has_data === false ? "empty" : null].filter(Boolean).join(" · ")
              : (w.owner_email ? `workspace: ${w.workspace}` : "workspace");
            return (
              <MenuItem key={w.workspace} onClick={() => chooseWorkspace(w.workspace)}
                sx={{ fontWeight: isCur ? 700 : 400 }}>
                {isCur && <CheckIcon fontSize="small" sx={{ mr: 1, color: "#2da894" }} />}
                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{label}</Typography>
                  <Typography variant="caption" sx={{ color: "#7a7f8a" }} noWrap>{caption}</Typography>
                </Box>
              </MenuItem>
            );
          }) : []),
          (!loadingWs && workspaces.length === 0) ? (
            <MenuItem key="__nows" disabled>
              <Typography variant="caption" sx={{ color: "#7a7f8a" }}>No spaces</Typography>
            </MenuItem>
          ) : null,
        ]}
      </Menu>
    </Box>
  );
}

// A full-height tab: label centered against the logo, teal underline flush with the bar's bottom
// when the route is active. NavLink supplies isActive; /memory matches only itself so the Account
// tab does not light up on /memory?tab=... and vice versa.
function NavTab({ to, label }) {
  return (
    <Box component={NavLink} to={to} end
         style={({ isActive }) => ({
           display: "flex", alignItems: "center", padding: "0 10px", textDecoration: "none",
           fontSize: 14, fontWeight: isActive ? 600 : 500, color: isActive ? "#fff" : "#b9bfcb",
           borderBottom: `2px solid ${isActive ? TEAL : "transparent"}`, marginBottom: -1,
         })}>
      {label}
    </Box>
  );
}

export default function MemoryNav({ spaceState = null, viewedWorkspace = null,
                                    onSelectSpace = () => {}, onOpenTeamPanel = () => {} }) {
  return (
    // One row from md up. On a phone the five pieces don't fit in one: the logo and the switchers keep
    // the top row and the tabs take a full-width row under it, so nothing pushes the page sideways.
    <Box sx={{ display: "flex", alignItems: "stretch", flexWrap: { xs: "wrap", md: "nowrap" },
               height: { xs: "auto", md: 56 }, columnGap: { xs: 1.5, md: 4 },
               px: { xs: 2, md: 4 }, bgcolor: "#0b0b0f", borderBottom: "1px solid #26262f" }}>
      <Box component={NavLink} to="/memory" aria-label="Assertion Memory"
           sx={{ display: "flex", alignItems: "center", minHeight: { xs: 52, md: "auto" } }}>
        <Box component="img" src={Logo} alt="Assertion"
             sx={{ height: 24, display: "block", filter: "brightness(0) invert(1)" }} />
      </Box>
      <Box component="nav" aria-label="Memory"
           sx={{ display: "flex", alignItems: "stretch", gap: 0.5, order: { xs: 3, md: 0 },
                 width: { xs: "100%", md: "auto" }, height: { xs: 44, md: "auto" }, ml: { xs: -1.25, md: 0 } }}>
        <NavTab to="/memory" label="Memory" />
        <NavTab to="/connect" label="Connections" />
        <NavTab to="/account" label="Account" />
      </Box>
      {/* Right group: the product switcher, then the user-facing space switcher (only when the
          page passes space data) and the admin-only org switcher — separate state channels. */}
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box component={NavLink} to="/" aria-label="Switch to Analytics"
             sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, mr: { xs: 0, md: 1 },
                   borderRadius: 1, border: "1px solid #26262f", color: "#b9bfcb", fontSize: 13,
                   textDecoration: "none", "&:hover": { color: "#fff", borderColor: "#3a3a46" } }}>
          <SwapHorizIcon sx={{ fontSize: 16 }} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Analytics</Box>
        </Box>
        <SpaceSwitcher spaceState={spaceState} viewedWorkspace={viewedWorkspace}
                       onSelectSpace={onSelectSpace} onOpenTeamPanel={onOpenTeamPanel} />
        <AdminOrgSwitcher />
      </Box>
    </Box>
  );
}
